import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Target, Search, Check, AlertTriangle, ShieldCheck, Car, FileText, Radio } from "lucide-react";
import { api, NODE_COLORS } from "../../services/api";

const COL = { source: 0, vahan: 0, vehicle: 1, install: 2, station: 2, cylinder: 3, cylinder_x: 3, certificate: 4, center: 5 };

function ContextGraph({ graph }) {
  const W = 760, H = 300, pad = 46;
  const byCol = {};
  graph.nodes.forEach((n) => {
    const c = COL[n.type] ?? 1;
    (byCol[c] = byCol[c] || []).push(n);
  });
  const cols = Math.max(...Object.keys(byCol).map(Number)) + 1;
  const pos = {};
  Object.entries(byCol).forEach(([c, ns]) => {
    const x = pad + (Number(c) / (cols - 1)) * (W - pad * 2);
    ns.forEach((n, i) => {
      const y = pad + ((i + 0.5) / ns.length) * (H - pad * 2);
      pos[n.id] = { x, y, n };
    });
  });
  return (
    <svg className="rv-graph" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {graph.edges.map((e, i) => {
        const a = pos[e.source], b = pos[e.target];
        if (!a || !b) return null;
        return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#cbd5e1" strokeWidth="1.3" />;
      })}
      {Object.values(pos).map(({ x, y, n }) => {
        const color = NODE_COLORS[n.type] || "#64748b";
        return (
          <g key={n.id}>
            <circle cx={x} cy={y} r="9" fill={color} stroke="#fff" strokeWidth="2" />
            <text x={x} y={y - 15} textAnchor="middle" fontSize="10.5" fill="#334155" fontWeight="600">{n.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function RVVehicle() {
  const { plate } = useParams();
  const [d, setD] = useState(null);
  const nav = useNavigate();

  useEffect(() => { setD(null); api.rvVehicle(plate).then(setD); }, [plate]);
  if (!d) return <div className="loading-page">Correlating vehicle, cylinder & certificate records…</div>;

  const m = d.state_meta;
  const ev = d.evidence;
  return (
    <div className="rv-vehicle" data-testid="rv-vehicle-page">
      <button className="link-btn" onClick={() => nav("/iglrv")} style={{ marginBottom: 12 }}><ArrowLeft size={14} /> Command Center</button>

      <div className="rv-banner" style={{ background: m.soft, borderColor: m.color, color: m.color }}>
        <i className={`rv-dot ${m.dot}`} />
        {d.state === "PRE-VALIDATED" ? "NO PRE-IDENTIFIED EXCEPTION" : d.state === "COMPLIANCE FLAG" ? "REFUEL — COMPLIANCE FLAG" : d.state === "VERIFICATION REQUIRED" ? "REFUEL VERIFICATION REQUIRED" : "UPCOMING COMPLIANCE RISK"}
      </div>

      <div className="rv-vehicle-grid">
        <div className="rv-card">
          <div className="rv-card-h"><Car size={15} /> Vehicle details</div>
          <dl className="rv-dl">
            <div><dt>Vehicle</dt><dd>{d.plate}</dd></div>
            <div><dt>Type</dt><dd>{d.type}</dd></div>
            <div><dt>Owner</dt><dd>{d.owner}</dd></div>
            <div><dt>Detected at</dt><dd>{d.station}{d.detected_at ? ` · ${d.detected_at}` : ""}</dd></div>
          </dl>
        </div>
        <div className="rv-card">
          <div className="rv-card-h"><Radio size={15} /> DotIQ context graph</div>
          <ContextGraph graph={d.graph} />
        </div>
      </div>

      <div className="rv-config-grid">
        <div className="rv-card">
          <div className="rv-card-h">Expected configuration</div>
          {d.expected_config.map((c) => (
            <div key={c.id} className={`rv-cfg ${c.status}`} data-testid={`rv-expected-${c.id}`}>
              {c.status === "valid" ? <Check size={14} /> : <AlertTriangle size={14} />}
              <b>{c.id}</b><span>{c.note}</span>
            </div>
          ))}
        </div>
        <div className="rv-card">
          <div className="rv-card-h">Observed configuration</div>
          {d.observed_config.length === 0 && <div className="rv-cfg valid"><span>Not yet observed at a station.</span></div>}
          {d.observed_config.map((c) => (
            <div key={c.id} className={`rv-cfg ${c.status === "match" ? "valid" : "exception"}`} data-testid={`rv-observed-${c.id}`}>
              {c.status === "match" ? <Check size={14} /> : <AlertTriangle size={14} />}
              <b>{c.id}</b><span>{c.status === "match" ? "Match" : "Not found in certified vehicle configuration"}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rv-card rv-decision">
        <div className="rv-card-h" style={{ color: m.color }}><Sparkles size={15} /> DotIQ's decision — why this vehicle was {d.state === "PRE-VALIDATED" ? "cleared" : "flagged"}</div>
        {d.explanation ? (
          <>
            <div className="ai-row"><div className="ai-icon"><Sparkles size={14} /></div><div><div className="ai-label">Why it matters</div><p>{d.explanation.why_it_matters}</p></div></div>
            <div className="ai-row"><div className="ai-icon rec"><Target size={14} /></div><div><div className="ai-label">Recommended action</div><p>{d.explanation.recommended_action}</p></div></div>
            <div className="ai-row"><div className="ai-icon"><Search size={14} /></div><div><div className="ai-label">Why this confidence</div><p>{d.explanation.confidence_rationale}</p></div></div>
          </>
        ) : (
          <div className="ai-row"><div className="ai-icon rec"><ShieldCheck size={14} /></div><div><div className="ai-label">Status</div><p>{d.reason}. {d.recommended_action}</p></div></div>
        )}
        <div className="rv-scorebar">
          <span>Risk score <b>{d.score}/100</b></span><span>Confidence <b>{d.confidence}%</b></span>
        </div>
      </div>

      <div className="rv-card-h" style={{ margin: "22px 0 10px", fontSize: 15 }}>Show the evidence</div>
      <div className="rv-evidence-grid">
        <div className="rv-ev-card">
          <div className="rv-ev-h"><Car size={14} /> VAHAN Vehicle</div>
          <div className="rv-ev-row"><span>Vehicle</span><b>{ev.vahan.vehicle}</b></div>
          <div className="rv-ev-row"><span>Fuel</span><b>{ev.vahan.fuel}</b></div>
          <div className="rv-ev-row"><span>Chassis</span><b>{ev.vahan.chassis}</b></div>
          <div className="rv-ev-row"><span>Status</span><b>{ev.vahan.status}</b></div>
        </div>
        <div className="rv-ev-card">
          <div className="rv-ev-h"><FileText size={14} /> PESO / Hydro-test</div>
          {ev.peso.map((p) => (
            <div key={p.cylinder} className="rv-ev-row">
              <span>{p.cylinder} → {p.cert_no}</span>
              <b className={p.status === "Valid" ? "ok" : "no"}>{p.status}</b>
            </div>
          ))}
        </div>
        <div className="rv-ev-card">
          <div className="rv-ev-h"><Radio size={14} /> Station observation</div>
          <div className="rv-ev-row"><span>Plate detected</span><b>{ev.station.plate_detected}</b></div>
          <div className="rv-ev-row"><span>Cylinders observed</span><b>{(ev.station.observed || []).join(", ") || "—"}</b></div>
        </div>
      </div>

      <div className="rv-correlation">
        <span>Correlation result</span>
        <code>Expected {`{${ev.correlation.expected.join(",")}}`}</code>
        <code>Observed {`{${(ev.correlation.observed || []).join(",") || "—"}}`}</code>
        {ev.correlation.exception.length > 0
          ? <code className="exc">Exception → {ev.correlation.exception.join(",")}</code>
          : <code className="ok">No exception</code>}
      </div>

      <footer className="vans-foot">DotIQ × IGL — Refuel Validation POC · synthetic VAHAN/PESO/ANPR data · "No pre-identified exception" is not a mechanical safety certification · IGL retains all refueling & verification policy.</footer>
    </div>
  );
}
